import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@apollo/client';
import * as S from '@/components/Ad/Status/Status.styles';
import { AdPageTestIds } from '@/__e2e__/testIds/ad-page';
import { AdStatus } from '@/types';
import { Button } from '@/components/UI/Button/Button';
import { useAuthStore } from '@/store/useAuthStore';
import { useAdDetails } from '@/views/Ads/useAdDetails';
import { GetUserResponse } from '@/api/types/user.types';
import { GET_USER } from '@/api/queries/user.queries';
import { PROLONG_AD } from '@/api/mutations/ads.mutations';
import {
  PayForAdByPrepaidBundleResponse,
  ProlongAdResponse,
} from '@/api/types/ads.types';
import { PAY_FOR_AD_BY_PREPAID_BUNDLE } from '@/api/mutations/payments.mutations';
import { useCurrentCategoryDetails } from '@/hooks/useCurrentCategory/useCurrentCategoryDetails';

interface StatusCardProps {
  status: AdStatus;
  isAuthor: boolean;
  onSuccess?: () => void;
  paymentFlowToggle: () => void;
  payByBalancePopupToggle: () => void;
  prolongByBalancePopupToggle: () => void;
}

export const StatusCard: React.FC<StatusCardProps> = ({
  status,
  isAuthor,
  onSuccess,
  paymentFlowToggle,
  payByBalancePopupToggle,
  prolongByBalancePopupToggle,
}) => {
  const { t } = useTranslation();

  const isAuthenticated = useAuthStore(state => state.isAuthenticated);
  const { categoryDetails } = useCurrentCategoryDetails();

  const { adDetails, refetch: refetchAdDetails } = useAdDetails();
  const { data: userInfo } = useQuery<GetUserResponse>(GET_USER);
  const [prolongAd, { loading: prolongAdLoading }] =
    useMutation<ProlongAdResponse>(PROLONG_AD, {
      onCompleted: data => {
        refetchAdDetails().then(() => {
          if (data.prolongAd.entity) {
            onSuccess?.();
          }
        });
      },
    });
  const [payForAdByPrepaidBundle, { loading: payForAdByPrepaidBundleLoading }] =
    useMutation<PayForAdByPrepaidBundleResponse>(PAY_FOR_AD_BY_PREPAID_BUNDLE, {
      onCompleted: () => {
        refetchAdDetails();
      },
    });

  const isB2B = isAuthenticated && userInfo?.currentUser?.isActiveShop;
  const isShopBalanceSufficient =
    !!userInfo?.currentUser && userInfo.currentUser.shopPaidAdsBalance >= 1;
  const isFreeLimitExceeded = !!categoryDetails?.isFreeLimitExceededForUser;

  function onPayButtonClick() {
    if (!adDetails || !userInfo?.currentUser) return;

    if (adDetails.isPayFromBundleAvailable) {
      if (isB2B) {
        payForAdByPrepaidBundle({ variables: { adId: adDetails.id } });
        return;
      }

      payByBalancePopupToggle();
      return;
    }

    paymentFlowToggle();
  }

  function onRestoreButtonClick() {
    if (!adDetails || !userInfo?.currentUser) return;

    if (isB2B) {
      if (
        adDetails.isPayFromBundleAvailable ||
        isShopBalanceSufficient ||
        adDetails.isProlongFree
      ) {
        prolongAd({ variables: { adId: adDetails.id } });
        return;
      }
      paymentFlowToggle();
      return;
    }

    if (adDetails.isProlongFree || !isFreeLimitExceeded) {
      prolongAd({ variables: { adId: adDetails.id } });
      return;
    }

    if (adDetails.isPayFromBundleAvailable) {
      prolongByBalancePopupToggle();
      return;
    }

    paymentFlowToggle();
  }

  if (status === AdStatus.Published) {
    return null;
  }

  const texts = {
    [AdStatus.Pending]: t('pages.ad.ad_pending'),
    [AdStatus.Expired]: t('pages.ad.ad_inactive'),
    [AdStatus.AwaitingPayment]: (
      <Trans
        i18nKey="pages.ad.ad_limit"
        values={{ categoryName: categoryDetails?.name }}
      />
    ),
    [AdStatus.Rejected]: (
      <>
        {t('pages.ad.ad_rejected')}
        {adDetails?.rejectReasons?.length ? (
          <ul>
            {adDetails?.rejectReasons?.map(el => {
              return <li key={el.id}>{el.template}</li>;
            })}
          </ul>
        ) : (
          <S.SingleRejectReason>{adDetails?.rejectReason}</S.SingleRejectReason>
        )}
      </>
    ),
    [AdStatus.Archived]: t('pages.ad.ad_archived'),
  };

  return (
    <>
      <S.Box
        $variant="1"
        $status={status}
        data-testid={AdPageTestIds.statusBanner.banner}
      >
        <S.StatusText data-testid={AdPageTestIds.statusBanner.text}>
          {texts[status]}
        </S.StatusText>

        {[AdStatus.Expired, AdStatus.Archived].includes(status) && isAuthor && (
          <Button
            data-testid={AdPageTestIds.statusBanner.button}
            appearance="secondaryLight"
            size="s"
            isFullWidth
            onClick={onRestoreButtonClick}
            disabled={prolongAdLoading}
          >
            {t('pages.ad.restore')}
          </Button>
        )}

        {status === AdStatus.AwaitingPayment && (
          <Button
            data-testid={AdPageTestIds.statusBanner.button}
            appearance="secondary"
            size="s"
            isFullWidth
            onClick={onPayButtonClick}
            disabled={payForAdByPrepaidBundleLoading}
          >
            {t('common.pay')}
          </Button>
        )}
      </S.Box>
    </>
  );
};
